import { CellState, Position, BoardConfig } from '../types';

/**
 * Represents the game board
 */
export class Board {
  private grid: CellState[][];
  private readonly size: number;

  constructor(size: number = 15) {
    this.size = size;
    this.grid = this.createEmptyGrid();
  }

  /**
   * Creates an empty grid
   */
  private createEmptyGrid(): CellState[][] {
    return Array(this.size)
      .fill(null)
      .map(() => Array(this.size).fill(CellState.EMPTY));
  }

  /**
   * Gets the board size
   */
  getSize(): number {
    return this.size;
  }

  /**
   * Gets the value of a cell
   */
  getCell(row: number, col: number): CellState {
    if (!this.isValidPosition(row, col)) {
      throw new Error(`Invalid position: (${row}, ${col})`);
    }
    return this.grid[row][col];
  }

  /**
   * Places a mark on the board
   */
  placeMove(row: number, col: number, mark: CellState): boolean {
    if (!this.isValidPosition(row, col)) {
      return false;
    }

    if (!this.isCellEmpty(row, col)) {
      return false;
    }

    this.grid[row][col] = mark;
    return true;
  }

  /**
   * Checks if a cell is empty
   */
  isCellEmpty(row: number, col: number): boolean {
    return (
      this.isValidPosition(row, col) && this.grid[row][col] === CellState.EMPTY
    );
  }

  /**
   * Checks if a position is valid
   */
  isValidPosition(row: number, col: number): boolean {
    return row >= 0 && row < this.size && col >= 0 && col < this.size;
  }

  /**
   * Checks if the board is full
   */
  isFull(): boolean {
    for (let row = 0; row < this.size; row++) {
      for (let col = 0; col < this.size; col++) {
        if (this.grid[row][col] === CellState.EMPTY) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Resets the board
   */
  reset(): void {
    this.grid = this.createEmptyGrid();
  }

  /**
   * Gets a copy of the grid (for display purposes)
   */
  getGrid(): CellState[][] {
    return this.grid.map((row) => [...row]);
  }

  /**
   * Gets display symbol for a cell
   */
  getCellDisplay(row: number, col: number): string {
    const cell = this.getCell(row, col);
    if (cell === CellState.PLAYER_X) return '🞩';
    if (cell === CellState.PLAYER_O) return '🞅';
    return '';
  }

  /**
   * Displays the board in the console
   */
  display(): void {
    console.log(
      '\n   ' +
        Array.from({ length: this.size }, (_, i) =>
          i.toString().padStart(2, ' ')
        ).join(' ')
    );
    console.log('  ┌' + '───'.repeat(this.size) + '┐');

    for (let row = 0; row < this.size; row++) {
      const rowStr = this.grid[row]
        .map((cell) => {
          if (cell === CellState.PLAYER_X) return ' 🞩 ';
          if (cell === CellState.PLAYER_O) return ' 🞅 ';
          return ' . ';
        })
        .join('');
      console.log(`${row.toString().padStart(2, ' ')}│${rowStr}│`);
    }

    console.log('  └' + '───'.repeat(this.size) + '┘\n');
  }
}
